import {BBox, Feature} from "geojson";
import RBush from "geojson-rbush";
import {bboxPolygon, lineIntersect} from "@turf/turf"
import {Id} from "@turf/helpers";
import {layer, source} from "azure-maps-control";
import {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";
import {GeomUtils} from "@esWebAPI/es/util/GeomUtils";
import {ImageSpriteGenerator} from "@esWebAPI/es/map/layer/ImageSpriteGenerator";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {LeaderLinePositionCalculator} from "@esWebAPI/es/map/module/LeaderLinePositionCalculator";
import {NavUtils} from "@esWebAPI/es/util/NavUtils";
import {LabelThematicStyler} from "@esWebAPI/es/map/styler/LabelThematicStyler";
import {LeaderLineLabelDimensionCalculator} from "@esWebAPI/es/map/module/LeaderLineLabelDimensionCalculator";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";
import {
    isBubbleLayer,
    isLabelBoxEnabled,
    isPieClusteringOn
} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import type {Map as AzureMap, SymbolLayerOptions, TextOptions} from "azure-maps-control";
import type {AzureBaseMap} from "@esWebAPI/es/map/module/AzureBaseMap";
import type {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import type {Dimensions, IEventChannel, LeaderLineConfig, PositionDirection, xy} from "@esWebAPI/es/model/Models";
import type {Properties} from "@turf/helpers/dist/js/lib/geojson";
import type {ILayerView} from "@esWebAPI/es/model/Layer";
import {IMapLayer, MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {HeatMapLayer} from "@esWebAPI/es/map/layer/HeatMapLayer";
import {RouteLayer} from "@esWebAPI/es/map/layer/route/RouteLayer";
import _ from "lodash";
import {PieClusterLayer} from "@esWebAPI/es/map/layer/PieClusterLayer";
import {PolygonLayer} from "@esWebAPI/es/map/layer/PolygonLayer";

// internal/private types
type ProposedPlacement = {
    isLeaderLineNeeded: boolean; // we only draw a leader line if the label is _not_ in the default/preferred position
    labelPosition: xy;
    positionDirection: PositionDirection;
    dimensions: Dimensions;
}

type ClusterPositionTracker = {
    clusterId: number;
    usedDirections: Set<string>;
}

/**
 * Handles rendering of LLL (Leader Line Labels) and, as much as possible, attempts to make them appear and behave to the user as standard labels.
 */
export class LeaderLineLabeler {

    private readonly baseMap: AzureBaseMap;
    private readonly featureViewModel: FeatureViewModel;
    private readonly _map: AzureMap;
    private readonly overlayModule: AzureOverlay | undefined;

    private labelLayerDs: source.DataSource;
    private leaderLineLayerDs: source.DataSource;

    private isInit = false;
    private isHeatMapLayerEnabled = false;
    private isPolygonLayer = false;

    private llConfigs: LeaderLineConfig[] = [];
    private llDimCalculator: LeaderLineLabelDimensionCalculator;

    public static AVERAGE_LABEL_DIMENSIONS = {width: 200, height: 40};

    // default and minimum val. is recalculated based on screen size.
    private static MAX_LABELS = 250;

    // circular iterations to run when attempting to find a position when there are more labels than we can reasonably hope to label (MAX_LABELS)
    private static readonly MIN_ATTEMPTS = 2;

    // --- runtime customizable render props start ---

    public static MAX_LABEL_WIDTH = parseInt(es.util.localStorage.get("LLL_MAX_LABEL_WIDTH") || "400");

    private static MAX_FEAT_QUERY_COUNT_TO_PROCESS = parseInt(es.util.localStorage.get("LLL_MAX_FEAT_QUERY_COUNT_TO_PROCESS") || "20000");

    // max circular iterations to run when attempting to find a position
    private static readonly MAX_ATTEMPTS = parseInt(es.util.localStorage.get("LLL_MAX_ATTEMPTS") || "4");

    private static readonly MAX_CLUSTER_BREAKOUT_SIZE = parseInt(es.util.localStorage.get("LLL_MAX_CLUSTER_BREAKOUT_SIZE") || "10");

    // max num clusters to fetch clusterLeaves for. note: be careful increasing this, especially with pbf
    // note: could have two vals, one for geojson and another pbf but for consistency will keep same for now
    private static readonly MAX_CLUSTER_BREAKOUT_ATTEMPTS = parseInt(es.util.localStorage.get("LLL_MAX_CLUSTER_BREAKOUT_ATTEMPTS") || "50");

    private static readonly FILTER_CRISSCROSS_LEADER_LINES = es.util.localStorage.get("LLL_FILTER_CRISSCROSS_LEADER_LINES") !== "false";

    // emergency exit - abandon labeling after this amount of time
    public static readonly MAX_PROCESSING_TIME = parseInt(es.util.localStorage.get("LLL_MAX_PROCESSING_TIME") || "1500");

    // always show LL even if label is in preferred position
    private static readonly ALWAYS_SHOW_LL = es.util.localStorage.get("LLL_ALWAYS_SHOW_LL") === "true";

    // width of LL
    private static readonly LL_STROKE_WIDTH = parseInt(es.util.localStorage.get("LLL_STROKE_WIDTH") || "1");

    // when onUpdateLayerComplete is triggered, it takes a little while longer for the map render to catch up
    // (especially with exporting mapbook) so we need to wait a bit longer to process after this event is fired.
    private static readonly ON_UPDATE_LAYER_COMPLETE_EVENT_DELAY = parseInt(es.util.localStorage.get("LLL_ON_UPDATE_LAYER_COMPLETE_EVENT_DELAY") || "500");

    // --- runtime customizable render props end ---


    // --- debug flags start ---
    private static readonly DEBUG_SHOW_STATISTICS = true;

    private static readonly DEBUG_SHOW_LABEL_DIRECTION_AND_ATTEMPT = es.util.localStorage.get("LLL_DEBUG_SHOW_LABEL_DIRECTION_AND_ATTEMPT") === "true";

    private static readonly DEBUG_SHOW_COLLISION_DETECTION = false;

    private static readonly DEBUG_SHOW_LABEL_BOUNDS = es.util.localStorage.get("LLL_DEBUG_SHOW_LABEL_BOUNDS") === "true";
    private static readonly DEBUG_SHOW_TER_LABEL_BOUNDS = es.util.localStorage.get("LLL_DEBUG_SHOW_TER_LABEL_BOUNDS") === "true";

    private static readonly DEBUG_COLLISION_BOUNDS_COLOR_CACHE: Map<string, number> = new Map();
    // --- debug flags end ---

    private readonly debouncedQueryRenderedFeatsAndLabelThem: () => void;
    private static readonly DEBOUNCE_QUERY_TIMEOUT = 1500; // 500 or 750?
    private static readonly DEBOUNCE_QUERY_OPTS = { // see: https://lodash.com/docs/4.17.15#debounce
        leading: true,
        trailing: true,
        maxWait: LeaderLineLabeler.DEBOUNCE_QUERY_TIMEOUT
    };

    private readonly debouncedReInitLabels: () => void;
    private static readonly DEBOUNCE_RE_INIT_TIMEOUT = 1000;

    private allAzLayerIds: string[] = [];
    private allAzLayerIdsLabelled: string[] = []; // makes cleaning up a bit easier

    private readonly eventChannel;

    private rbush = RBush();

    constructor(
        baseMap: AzureBaseMap,
        featureViewModel: FeatureViewModel,
        overlayModule: AzureOverlay | undefined,
        eventChannel: IEventChannel
    ) {
        this.baseMap = baseMap;
        this._map = baseMap.map;
        this.featureViewModel = featureViewModel;
        this.overlayModule = overlayModule;
        this.eventChannel = eventChannel;

        this.debouncedQueryRenderedFeatsAndLabelThem = _.debounce(() => {
            // ensure tiles are loaded before labeling
            if (this._map.areTilesLoaded()) {
                //es.log.log("debouncedQueryRenderedFeatsAndLabelThem", "tilesLoaded");
                this.queryRenderedFeatsAndLabelThem();
            } else {
                // wait for tiles to load
                const onDataOrIdle = () => {
                    //es.log.log("debouncedQueryRenderedFeatsAndLabelThem", "onDataOrIdle done");
                    this._map.events.remove('data', onDataOrIdle);
                    this._map.events.remove('idle', onDataOrIdle);
                    this.queryRenderedFeatsAndLabelThem();
                };
                this._map.events.addOnce('data', onDataOrIdle);
                this._map.events.addOnce('idle', onDataOrIdle);
            }
        }, LeaderLineLabeler.DEBOUNCE_QUERY_TIMEOUT, LeaderLineLabeler.DEBOUNCE_QUERY_OPTS);

        this.debouncedReInitLabels = _.debounce(() => {
            this.reInitLabels();
        }, LeaderLineLabeler.DEBOUNCE_RE_INIT_TIMEOUT);

        this.llDimCalculator = new LeaderLineLabelDimensionCalculator();

        // note: we don't remove this listener as it listens to see when we need to init/destroy
        this.eventChannel.addListener("onUpdateLayerComplete", this.doOnUpdateLayerComplete, this);
    }

    private doOnUpdateLayerComplete(layer: IMapLayer): void {
        //es.log.log("LLL doOnUpdateLayerComplete", layer);
        const isLoadedAlready = this.allAzLayerIdsLabelled.includes("lll_line_" + layer.layerView.id);

        const isLLL = layer.layerView.styleSettings?.labelSettings?.enabled &&
            layer.layerView.styleSettings?.labelSettings?.labelling === "LEADER_LINES";

        // note: this is fragile, there might be cases where it isn't right e.g. cases where it's getting triggered too much
        if (isLoadedAlready && !isLLL) {
            // LLL was enabled and user has disabled it
            this.destroy();
        } else if(!isLoadedAlready && isLLL) {
            // LLL was disabled and user has disabled it
            this.reInitLabels();
        } else if(isLoadedAlready && isLLL){
            //this.reInitLabels();
            //this.reRenderLabels();
            
            // user is probably on either:
            //     a) in style page toggling things
            //     b) in a map page toggling things in the control panel
            //     c) printing a flipping mapbook
            // I think in all cases but c, we want to tear down and start again

            setTimeout(() => {
                es.log.log(`doOnUpdateLayerComplete and waited ${LeaderLineLabeler.ON_UPDATE_LAYER_COMPLETE_EVENT_DELAY}ms`);
                //this.reRenderLabels();
                this.reInitLabels();
            }, LeaderLineLabeler.ON_UPDATE_LAYER_COMPLETE_EVENT_DELAY);
        }
    }

    private addListeners(): void {
        this.eventChannel.addListener("onAddLayer", this.debouncedReInitLabels, this);
        this.eventChannel.addListener("onRemoveLayer", this.debouncedReInitLabels, this);
        this.eventChannel.addListener("onSetBaseMap", this.debouncedReInitLabels, this);
        this.eventChannel.addListener("onFeatureViewUpdate", this.debouncedReInitLabels, this);

        //this.eventChannel.addListener('onFeatureViewModelUpdateLayer', this.reInitLabels, this);
        //this.eventChannel.addListener("onUpdateLayerIndex", this.reInitLabels, this);
        //this.eventChannel.addListener("onFeatureViewUpdate", this.reInitLabels, this);
        //this.eventChannel.addListener("onUpdateLayer", this.reRenderLabels, this);

        this.eventChannel.addListener("onSyncDatasetFinishAndFeatureViewModelUpdated", this.debouncedQueryRenderedFeatsAndLabelThem, this);
        this.eventChannel.addListener("onUpdateFeature", this.debouncedQueryRenderedFeatsAndLabelThem, this);
        this.eventChannel.addListener("onCommitFeatureEdits", this.debouncedQueryRenderedFeatsAndLabelThem, this);
        this.eventChannel.addListener("onRevertFeatureEdits", this.debouncedQueryRenderedFeatsAndLabelThem, this);
        //this.eventChannel.addListener("onTerritoryLabelsUpdate", this.debouncedQueryRenderedFeatsAndLabelThem, this);

        es.api.getEventChannel().addListener("onUpdateLayerViewRemovableCriteria", this.debouncedQueryRenderedFeatsAndLabelThem, this);
    }

    public destroy(): void {
        es.log.log("LLL destroy");
        this.isInit = false;

        // listeners
        this._map.events.remove('moveend', this.debouncedQueryRenderedFeatsAndLabelThem);

        this.eventChannel.removeListener('onAddLayer', this.debouncedReInitLabels);
        this.eventChannel.removeListener('onRemoveLayer', this.debouncedReInitLabels);
        this.eventChannel.removeListener("onSetBaseMap", this.debouncedReInitLabels);
        this.eventChannel.removeListener("onFeatureViewUpdate", this.debouncedReInitLabels);

        //this.eventChannel.removeListener('onFeatureViewModelUpdateLayer', this.reInitLabels);
        //this.eventChannel.removeListener("onUpdateLayerIndex", this.reInitLabels);
        //this.eventChannel.removeListener("onFeatureViewUpdate", this.reInitLabels);
        //this.eventChannel.removeListener("onUpdateLayer", this.reRenderLabels);

        this.eventChannel.removeListener("onSyncDatasetFinishAndFeatureViewModelUpdated", this.debouncedQueryRenderedFeatsAndLabelThem);
        this.eventChannel.removeListener("onUpdateFeature", this.debouncedQueryRenderedFeatsAndLabelThem);
        this.eventChannel.removeListener("onCommitFeatureEdits", this.debouncedQueryRenderedFeatsAndLabelThem);
        this.eventChannel.removeListener("onRevertFeatureEdits", this.debouncedQueryRenderedFeatsAndLabelThem);
        //this.eventChannel.removeListener("onTerritoryLabelsUpdate", this.debouncedQueryRenderedFeatsAndLabelThem);

        this.allAzLayerIdsLabelled.forEach(azLayerId => {
            if(this._map.layers.getLayerById(azLayerId)){
                this._map.layers.remove(azLayerId);
            }
        });

        this.allAzLayerIds = [];
        this.allAzLayerIdsLabelled = [];
        this.llConfigs = [];

        // datasources
        const dsIds = [
            "leaderLineLabelLabels",
            "leaderLineLabelLines",
        ];

        dsIds.forEach(dsId => {
            if(this._map.sources.getById(dsId)){
                //(this._map.sources.getById(dsId) as DataSource).clear()
                this._map.sources.remove(dsId);
            }
        });
    }

    private async reInitLabels(): Promise<void> {
        es.log.log("reInitLabels");
        this.destroy();
        await this.init();
    }

    private reRenderLabels(): void {
        es.log.log("reRenderLabels");
        this.debouncedQueryRenderedFeatsAndLabelThem();
    }

    /**
     * init LLL for any (if any) layers with LLL enabled.
     * @return promise returns ids of layerViews with LLL enabled (layersWithLeaderLineLabels)
     */
    async init(): Promise<number[]> {

        const layersWithLeaderLineLabels: ILayerView[] = this.featureViewModel.listLayers()
            .map(l => l.getLayerView())
            .filter(layer => layer.styleSettings?.labelSettings?.labelling === "LEADER_LINES" && layer.styleSettings?.labelSettings?.enabled);

        if (!layersWithLeaderLineLabels.length) {
            //es.log.log("labelRenderedPoints : no layersWithLeaderLineLabels enabled");
            return Promise.resolve([]);
        }

        // todo: recalculate if screen size changes - need to save map dimensions and listen to onUpdateView and compare map dimensions with our saved
        this.calculateMaxLabels();

        // create datasources
        this.leaderLineLayerDs = new source.DataSource("leaderLineLabelLines", {
            cluster: false,
            maxZoom: 22
        });
        this.labelLayerDs = new source.DataSource("leaderLineLabelLabels", {
            cluster: false,
            maxZoom: 22
        });

        this._map.sources.add([this.leaderLineLayerDs, this.labelLayerDs]);

        this.allAzLayerIds = this.featureViewModel.listLayers()
            .map(layer => layer.getLayerView())
            .filter(layerView => {
                const isLLL = layerView.styleSettings?.labelSettings?.enabled &&
                    layerView.styleSettings?.labelSettings?.labelling === "LEADER_LINES";

                return isLLL;
            })
            .flatMap(layerView => {

                const mapLayer = this.baseMap.getLayersByLayerViewId(layerView.id)[0] as MapLayer;

                if (!mapLayer) {
                    es.log.warn("getLayersByLayerViewId layer not found", layerView.id, layerView.title);
                    return [];
                }

                if (mapLayer instanceof PointLayer) {
                    const isBubble = isBubbleLayer(mapLayer.layerView, mapLayer.layerInfo);
                    if (isBubble) {
                        // Return both IDs for numbercluster layers
                        if (isPieClusteringOn(mapLayer.layerView)) {
                            return [
                                mapLayer.bubbleLayer.getLayerId(),
                                mapLayer.pieClusterLayer?.getActiveAzureLayerId()
                            ];
                        }
                    } else if (!isBubble) {
                        return [mapLayer.symbolLayer.getLayerId()];
                    }


                } else if (mapLayer instanceof RouteLayer) {
                    // Return both IDs for schedule layers
                    return [
                        mapLayer.pointLayer?.getActiveAzureLayerId(),
                        mapLayer.stopLayer?.getActiveAzureLayerId()
                    ];
                } else if (mapLayer instanceof PolygonLayer) {
                    this.isPolygonLayer = true;
                }

                return [mapLayer.getActiveAzureLayerId()];

            })
            .filter(Boolean);

        es.log.log("this.allAzLayerIds", this.allAzLayerIds);

        // Wait for all LLL datasource to be ready before proceeding
        const dsReadyPromises: Promise<source.Source>[] = [];
        const lvIds = layersWithLeaderLineLabels.map(lv => lv.id);
        this.baseMap.getDatasourceManager().getDatasourceIds(lvIds).forEach(dsId => {
            dsReadyPromises.push(this.baseMap.getDatasourceManager().onDataSourceReady(dsId));
        });
        await Promise.all(dsReadyPromises);

        const imageSpriteGenerator = new ImageSpriteGenerator(new AzureImageSpriteManager(this._map));

        this.llConfigs = [];

        // Create configurations for each layer
        for (const layerView of layersWithLeaderLineLabels) {

            let mapLayer =  this.baseMap.getLayersByLayerViewId(layerView.id)[0] as PointLayer | RouteLayer | HeatMapLayer | PieClusterLayer;
            if(!mapLayer){
                es.log.error("LLL : could not find layer in basemap", layer);
                continue;
            }

            const azLabelLayerId = "lll_label_" + layerView.id;
            const azLineLayerId = "lll_line_" + layerView.id;

            let colorThematic = mapLayer?.colorThematic;

            const textOptions = mapLayer?.labelLayer?.getOptions()?.textOptions ||
                mapLayer?.pointLayer?.labelLayer?.getOptions()?.textOptions;

            if (!textOptions) {
                // labelling is about to fail. check layer type has exposed the correct point and label layer, especially if the layer is a "composite" type e.g. RouteLayer
                es.log.error("Unable to get text options for layer", layerView);
            }

            textOptions.textField = ['case',
                ['has', 'ES_LABEL'],
                ['get', 'ES_LABEL'],
                textOptions?.textField ?? ''
            ];

            const llColor =  es.util.color.serverToHex(layerView.styleSettings?.defaultStyle?.text?.leaderLineColor || es.util.color.BLACK);

            const leaderLineLayer = new layer.LineLayer(this.leaderLineLayerDs, azLineLayerId, {
                strokeColor: llColor, // "red", // "#2E3C8C", // azLayerLabelOpts.color, // todo: handle inheritColor? use a different color than azLayerLabelOpts.color e.g. black?
                strokeWidth: LeaderLineLabeler.LL_STROKE_WIDTH,
                filter: ['==', ['get', 'lvId'], layerView.id] // Add filter to only show relevant lines
            });

            // see: https://learn.microsoft.com/en-us/javascript/api/azure-maps-control/atlas.textoptions?view=azure-maps-typescript-latest
            const labelTextOpts = {
                textField: textOptions.textField,
                offset: [0, 0],
                font: textOptions.font,
                size: textOptions.size,
                color: textOptions.color,
                haloBlur: textOptions.haloBlur,
                //haloBlur: 3,
                haloColor: textOptions.haloColor,
                //haloColor: "white",
                haloWidth: textOptions.haloWidth + 1,
                //haloWidth: 5,
                allowOverlap: false,   // Specifies if the text will be visible if it collides with other symbols. If true, the text will be visible even if it collides with other previously drawn symbols. Default false.
                ignorePlacement: true, // Specifies if the other symbols are allowed to collide with the text. If true, other symbols can be visible even if they collide with the text. Default false.
                optional: false,       // Specifies if the text can be hidden if it is overlapped by another symbol. If true, icons will display without their corresponding text when the text collides wit other symbols and the icon does not. Default false.
            } as TextOptions;

            const labelLayer = new layer.SymbolLayer(this.labelLayerDs, azLabelLayerId, {
                textOptions: labelTextOpts,
                iconOptions: {
                    image: isLabelBoxEnabled(mapLayer.layerView) ?
                        imageSpriteGenerator.generateBoxImage() : 'none',
                    anchor: 'center',
                    allowOverlap: false,
                    ignorePlacement: true,
                    optional: false,
                },
                // add filter to only show relevant labels
                filter: ['==',
                    ['get', 'lvId'],
                    layerView.id
                ]
            } as SymbolLayerOptions);

            const labelStyler = new LabelThematicStyler(
                layerView,
                mapLayer.layerInfo.attributes,
                undefined,
                undefined,
                colorThematic
            );

            //Azure does not expose the icon-text-fit property so we need to set it in the MapLibre map once the layer is loaded
            this._map.events.add("layeradded", (layer) => {
                if (labelLayer && layer.getId() === labelLayer.getId()) {
                    this.applyMapLibreBoxIconsOptions(layer.getId(), labelStyler);
                }
            });

            if(this._map.layers.getLayerById(azLineLayerId)){
                this._map.layers.remove(azLineLayerId);
            }
            if(this._map.layers.getLayerById(azLabelLayerId)){
                this._map.layers.remove(azLabelLayerId);
            }

            this._map.layers.add(leaderLineLayer);
            this._map.layers.add(labelLayer);
            this.allAzLayerIdsLabelled.push(...[azLabelLayerId, azLineLayerId]);

            // https://learn.microsoft.com/en-sg/answers/questions/2069653/azure-maps-how-to-prevent-text-from-wrapping
            //this._map.map.setLayoutProperty(azLabelLayerId, 'text-max-width', 500);
            this._map.map.setLayoutProperty(labelLayer.getId(), 'text-max-width', LeaderLineLabeler.MAX_LABEL_WIDTH);

            // todo: get proper label
            const labelAttr = layerView.styleSettings?.labelSettings?.labelAttrs[0];

            const defaultStyle = layerView.styleSettings.defaultStyle;

            // force central for polygon. sometimes the setting can either be BELOW or CENTRAL but only CENTRAL IS supported
            const basicTextPosition = layerView.geomType === "POLYGON" ? "CENTRAL" : defaultStyle.text?.position || "BELOW";

            // Get point radius from style
            const featSize = defaultStyle?.size || defaultStyle?.symbolStyle?.size || 10; // use 10 for polygon centre point

            // Store the configuration
            this.llConfigs.push({
                lvId: layerView.id,
                leaderLineLayer,
                labelLayer,
                labelAttr,
                mapLayer: mapLayer,
                basicTextPosition,
                featSize
            } as LeaderLineConfig);
        }

        this.addListeners();

        // wait for lll to be rendered before querying rendered feats
        return new Promise<number[]>(resolve => {
            let timeoutId;
            const completeInit = () => {
                clearTimeout(timeoutId);
                if(this.isInit){
                    es.log.warn("completeInit called when isInit = true");
                    return;
                }
                this.isInit = true;

                this.debouncedQueryRenderedFeatsAndLabelThem();
                this._map.events.add('moveend', this.debouncedQueryRenderedFeatsAndLabelThem);
                es.log.log("LeaderLineLabeler: init complete", this.llConfigs);
                const lllLvIds = layersWithLeaderLineLabels.map(l => l.id)
                resolve(lllLvIds);
            };

            if (this._map.areTilesLoaded()) {
                if(this.isHeatMapLayerEnabled){
                    timeoutId = setTimeout(() => {
                        completeInit();
                    }, LeaderLineLabeler.MAX_PROCESSING_TIME);
                } else {
                    this._map.events.addOnce("idle", completeInit);
                    timeoutId = setTimeout(() => {
                        es.log.warn(`idle event didn't fire after ${LeaderLineLabeler.MAX_PROCESSING_TIME}ms. Completing init anyway`);
                        completeInit();
                    }, LeaderLineLabeler.MAX_PROCESSING_TIME);
                }
            } else {
                // wait for the next "idle" event
                this._map.events.addOnce("idle", completeInit);
            }
        });
    }

    /**
     * Calculate maximum number of labels based on map dimensions
     */
    private calculateMaxLabels(): void {
        const [width, height] = this.baseMap.getSizePx();

        // average label dimensions - doesn't need to be exact really
        // todo: use LeaderLineLabeler.AVERAGE_LABEL_DIMENSIONS 
        const LABEL_WIDTH = 120;
        const LABEL_HEIGHT = 20;
        const MAX_COVERAGE = 0.7; // 70% screen coverage

        const screenArea = width * height;
        const labelArea = LABEL_WIDTH * LABEL_HEIGHT;

        LeaderLineLabeler.MAX_LABELS = Math.max(
            LeaderLineLabeler.MAX_LABELS,
            Math.floor((screenArea * MAX_COVERAGE) / labelArea)
        );

        es.log.log("calculateMaxLabels", `${width}w x ${height}h`, LeaderLineLabeler.MAX_LABELS);
    }

    async queryRenderedFeatsAndLabelThem() {
        const startTime = Date.now();
        await this.labelFeats();
        const totalTime = Date.now() - startTime;
        es.log.log("queryRenderedFeatsAndLabelThem", this.allAzLayerIds, "total time", es.util.formatMillis(totalTime));
        // todo: should we wait for the map idle event here before firing the evt?
        this.eventChannel.fireEvent("onLeaderLineLabelsRendered", "queryRenderedFeatsAndLabelThem");
    }

    /**
     * Get a different colour from a palette per feature so we can see what's going on (max of about 20 or whatever the palette used size is) after which black will be used
     * @param featKey
     * @param serverColor
     */
    getDebugPolygonStyle(featKey: string, serverColor: number | undefined): string {
        if(!serverColor){
            const palette = es.util.color.palettes["iWantHue30"];
            if(LeaderLineLabeler.DEBUG_COLLISION_BOUNDS_COLOR_CACHE.has(featKey)){
                serverColor = LeaderLineLabeler.DEBUG_COLLISION_BOUNDS_COLOR_CACHE.get(featKey);
            } else {
                const cacheSize = LeaderLineLabeler.DEBUG_COLLISION_BOUNDS_COLOR_CACHE.size;
                if(cacheSize < palette.length - 1){
                    serverColor = es.util.color.serverFromHex(palette[cacheSize]);
                    LeaderLineLabeler.DEBUG_COLLISION_BOUNDS_COLOR_CACHE.set(featKey, serverColor);
                }
            }
        }
        return AzureOverlay.getDefaultPolygonStyle(serverColor);
    }

    /**
     * Draw debug bounds for an eS feat note: featKey is only used as an id so a unique colour is used for each feat. This
     * is useful when debugging attempts at finding a position for a single feat.
     * @param featKey
     * @param proposedBounds
     * @param serverColor
     * @param clear
     * @private
     */
    private drawDebugFeatBounds(featKey: string, proposedBounds: BBox, serverColor?: number | undefined, clear = 2000): void {
        const min = NavUtils.pixelToGeo(this.baseMap, {x: proposedBounds[0], y: proposedBounds[1]});
        const max = NavUtils.pixelToGeo(this.baseMap, {x: proposedBounds[2], y: proposedBounds[3]});

        this.overlayModule?.drawBounds(
            [min.lng, min.lat, max.lng, max.lat],
            this.getDebugPolygonStyle(featKey, serverColor),
            clear
        );
    }

    private drawDebugPixelBounds(pixelBounds: BBox, serverColor?: number | undefined, clear = 2000): void {
        const min = NavUtils.pixelToGeo(this.baseMap, {x: pixelBounds[0], y: pixelBounds[1]});
        const max = NavUtils.pixelToGeo(this.baseMap, {x: pixelBounds[2], y: pixelBounds[3]});

        this.overlayModule?.drawBounds(
            [min.lng, min.lat, max.lng, max.lat],
            AzureOverlay.getDefaultPolygonStyle(serverColor),
            clear
        );
    }

    /**
     * Calculate label position to avoid overlap (i.e. collision detection)
     */
    calculateLabelPosition(
        feat: Feature,
        llConfig: LeaderLineConfig,
        maxAttempts: number,
        clusterTracker: ClusterPositionTracker | null
    ): ProposedPlacement | undefined {

        if (!llConfig) {
            throw new Error("missing mandatory param llConfig");
        }

        const featCentrePoint = GeomUtils.getFeatCentrePoint(feat);
        const pixelPoint = NavUtils.geoToPixel(this.baseMap, featCentrePoint);
        const featKey = feat?.layer?.source + "_" + feat.properties?.ES_ID;

        const labelLayer = this._map.map.getLayer(llConfig.labelLayer.getId()); // todo: add this to the llConfig?
        const dimensions = this.llDimCalculator.getLabelDimensions(feat, llConfig, labelLayer);

        let featRadius = Math.floor(llConfig.featSize / 2);

        // todo: get the size from the size expression but since the size range is 10px-50px I'm just going to call it 10px for now
        // return SizeThematicStyler.getThematic(this.layerView).then(sizeThematic => {
        //     this._sizeThematic = sizeThematic;
        // });
        // const sizeThematicStyler = new SizeThematicStyler(llConfig.azureLayer?.layerView?.styleSettings, this._sizeThematic);
        // Calculate total available positions based on maxAttempts
        if(llConfig.mapLayer.layerView?.styleSettings?.sizeThematicSettings?.enabled){
            featRadius = 10;
        }

        const totalPositions = LeaderLinePositionCalculator.getTotalPositionCount(maxAttempts);
        for (let i = 0; i < totalPositions; i++) {
            const pos = LeaderLinePositionCalculator.getPositionForAttempt(featRadius, dimensions, i, llConfig.basicTextPosition);

            // skip if no direction found or it's already been used in this cluster
            if (!pos || clusterTracker?.usedDirections.has(pos.direction)) {
                continue;
            }

            const proposedPosition = {
                x: pixelPoint.x + pos.offset.x,
                y: pixelPoint.y + pos.offset.y
            };

            const proposedBounds = [
                proposedPosition.x - (dimensions.width / 2),  // minX
                proposedPosition.y - (dimensions.height / 2), // minY
                proposedPosition.x + (dimensions.width / 2),  // maxX
                proposedPosition.y + (dimensions.height / 2)  // maxY
            ];

            const collisions = this.getCollisions(proposedBounds);
            let isLabelOverlapping = false;
            for(const collisionBounds of collisions) {
                // special-case with central positioned labels: a label is allowed to overlap with its own point
                // if it has featKey property, it's a point bound todo: be more explicit and add type: point or something?
                if (collisionBounds?.properties?.featKey) {
                    if (llConfig.basicTextPosition == "CENTRAL" &&
                        collisionBounds?.properties?.featKey === featKey) {
                        // found collision with own point. need to keep checking other collisions
                        continue;
                    } else {
                        // collision with different point
                        isLabelOverlapping = true;
                        break;
                    }
                } else {
                    // no featKey means it's a label bound - this is always a collision
                    isLabelOverlapping = true;
                    break;
                }
            }

            if(LeaderLineLabeler.DEBUG_SHOW_COLLISION_DETECTION){
                for(const collisionBounds of collisions) {
                    this.drawDebugFeatBounds(featKey, collisionBounds?.bbox);
                }
            }

            if (!isLabelOverlapping) {

                if (LeaderLineLabeler.DEBUG_SHOW_LABEL_BOUNDS) {
                    this.drawDebugPixelBounds(proposedBounds, es.util.color.WHITE, -1);
                }

                clusterTracker?.usedDirections.add(pos.direction);

                const isPreferredPosition =
                    llConfig.basicTextPosition === "BELOW" && pos.direction === "S" && pos.attempt === 1 ||
                    llConfig.basicTextPosition === "ABOVE" && pos.direction === "N" && pos.attempt === 1 ||
                    llConfig.basicTextPosition === "CENTRAL" && pos.direction === "S" && pos.attempt === 1;

                const pp = {
                    isLeaderLineNeeded: LeaderLineLabeler.ALWAYS_SHOW_LL || !isPreferredPosition, // don't draw a leader line if the label is in the default/preferred position
                    labelPosition: proposedPosition,
                    positionDirection: pos,
                    dimensions
                } as ProposedPlacement;

                //es.log.log("Found ProposedPlacement", pp);
                return pp;
            }
        }
        return undefined;
    }

    /**
     * Shorten a line by given number of pixels from the startCoord side.
     * note: we need to do this is that we get collision detection conflicts with leader lines from a cluster as they will converge on the same point.
     * @param startCoord
     * @param endCoord
     * @param offsetPixels
     * @private
     */
    private shortenLeaderLine(
        startCoord: [number, number],
        endCoord: [number, number],
        offsetPixels: number = 1
    ): [number, number] {

        const pointPx = NavUtils.geoToPixel(this.baseMap, startCoord);
        const labelPx = NavUtils.geoToPixel(this.baseMap, endCoord);

        const newStartPx = GeomUtils.shortenLine(pointPx, labelPx, offsetPixels);
        const newPointGeo = NavUtils.pixelToGeo(this.baseMap, newStartPx);

        return [newPointGeo.lng, newPointGeo.lat];
    }

    /**
     * calc leader line length in pixels. use as one of the metrics for how good the placements compare when changes
     * are made, the idea being, the more labels and the shorter the leader lines the better.
     * @param startCoord
     * @param endCoord
     * @private
     */
    private getLeaderLineLengthPixels (
        startCoord: [number, number],
        endCoord: [number, number],
    ): number {
        const pointPx = NavUtils.geoToPixel(this.baseMap, startCoord);
        const labelPx = NavUtils.geoToPixel(this.baseMap, endCoord);
        return GeomUtils.getLineLength(pointPx, labelPx);
    }

    private applyMapLibreBoxIconsOptions(labelLayerId: string|undefined, labelThematicStyler: LabelThematicStyler ) {
        if (!labelLayerId) {
            es.log.warn("applyMapLibreBoxIconsOptions called with no layerId!");
            return;
        }
        // @ts-expect-error
        const mapLibreMap = this._map.map;
        mapLibreMap.setLayoutProperty(labelLayerId, "icon-text-fit", "both");
        mapLibreMap.setPaintProperty(labelLayerId, "icon-color", labelThematicStyler.getBoxFillColor());
        mapLibreMap.setPaintProperty(labelLayerId, "icon-halo-color", labelThematicStyler.getBoxOutlineColor());
        mapLibreMap.setPaintProperty(labelLayerId, "icon-halo-width", 3);
        mapLibreMap.setPaintProperty(labelLayerId, "icon-halo-blur", 0);
    }

    private getBoundsFromPlacement(centre: xy, dimensions: Dimensions): BBox {
        const bounds = [
            centre.x - (dimensions.width / 2),  // minX
            centre.y - (dimensions.height / 2), // minY
            centre.x + (dimensions.width / 2),  // maxX
            centre.y + (dimensions.height / 2)  // maxY
        ];
        return bounds;
    }

    /**
     * get bounds of each territory label so we avoid overlap with them in our lll collision detection
     * @private
     */
    private setupTerLabelBoundsForCollisionDetection(): void {

        if(!this._map.map.getLayer('territory-label-layer')){
            //es.log.log("setupTerLabelBoundsForCollisionDetection : there is no territory-label-layer. skipping");
            return;
        }

        const territoryLabelFeats = this._map.map.queryRenderedFeatures({
            layers: ['territory-label-layer']
        });

        for(const feat of territoryLabelFeats){
            const point = NavUtils.geoToPixel(this.baseMap, feat.geometry.coordinates);

            const textSize = this._map.map.getLayer('territory-label-layer').getLayoutProperty('text-size') ||
                LeaderLineLabeler.AVERAGE_LABEL_DIMENSIONS.width;

            const text = feat.properties.title || feat.properties.label || 'n/a';
            //const width = Math.min(text.length * (textSize * 0.6), LeaderLineLabeler.AVERAGE_LABEL_DIMENSIONS.width);
            //const height = Math.min(textSize * 1.2, LeaderLineLabeler.AVERAGE_LABEL_DIMENSIONS.height);
            const width = text.length * (textSize * 0.7);
            const height = textSize * 1.4;
            const minPixel = {
                x: point.x - (width / 2),
                y: point.y - (height / 2)
            };

            const maxPixel = {
                x: point.x + (width / 2),
                y: point.y + (height / 2)
            };

            const pixelBounds = [
                minPixel.x,
                minPixel.y,
                maxPixel.x,
                maxPixel.y
            ];

            if(LeaderLineLabeler.DEBUG_SHOW_TER_LABEL_BOUNDS){
                this.drawDebugPixelBounds(pixelBounds, es.util.color.serverFromHex("#04f5ec"), -1);
                es.log.log("terLabelBounds", text, pixelBounds, width, height, feat);
            }
            this.addBounds(pixelBounds);
        }

        //es.log.log("setupTerLabelBoundsForCollisionDetection", territoryLabelFeats);
    }

    /**
     * Create leader lines and labels for points on screen
     * todo: break this method up a bit
     */
    async labelFeats() : Promise<number> {

        const startTime = Date.now();

        let features: Feature[] = this._map.map.queryRenderedFeatures({
            layers: this.allAzLayerIds
        });

        // polygon layers can return dup feats when the feat is rendered on more than one tile
        // we only want to dedup if necessary i.e.
        if (this.isPolygonLayer) {
            const seen = new Set();
            features = features.filter(f => {
                const featKey = f.source + "_" + f.id;
                if (seen.has(featKey)) {
                    return false;
                }
                seen.add(featKey);
                return true;
            });
        }

        const queryRenderedFeaturesTime = (Date.now() - startTime) + "ms";

        this.leaderLineLayerDs.clear();
        this.labelLayerDs.clear();
        if (LeaderLineLabeler.DEBUG_SHOW_LABEL_BOUNDS || LeaderLineLabeler.DEBUG_SHOW_TER_LABEL_BOUNDS || LeaderLineLabeler.DEBUG_SHOW_COLLISION_DETECTION) {
            this.overlayModule?.clear();
        }
        this.rbush.clear();
        this.llDimCalculator.clearContext();

        this.setupTerLabelBoundsForCollisionDetection();

        const labelFeatures = [];
        const leaderLineFeats = [];

        const clusterBreakoutPromises: Promise<any>[] = [];
        const breakoutClusterFeats: Feature[] = [];

        const expandedClusters: number[] = [];
        const skippedClusters: number[] = [];

        const queryRenderedFeatCount = features.length;

        if(queryRenderedFeatCount > LeaderLineLabeler.MAX_FEAT_QUERY_COUNT_TO_PROCESS){
            es.log.warn(`Abandoning lll process as MAX_FEAT_QUERY_COUNT_TO_PROCESS (${LeaderLineLabeler.MAX_FEAT_QUERY_COUNT_TO_PROCESS}) exceeded (${queryRenderedFeatCount}) for layers ${this.allAzLayerIds}`);
            return queryRenderedFeatCount;
        }

        let crissCrossCount = 0;
        let totalLeaderLinePixelLength = 0;

        //const clusterCount = features.filter(feat => feat.properties?.cluster).length;
        const clusterCount = features.reduce((count, feat) => count + (feat.properties?.cluster ? 1 : 0), 0); // 'reduce' is faster than 'filter' which creates a new array
        const nonClusterCount = features.length - clusterCount;
        let bigClusterBreakoutCount = 0;

        // first, collect all point positions for collision-detection and breakout feats from clusters where possible (< 5)
        for (const feat of features) {
            const featLvId = this.baseMap.getLayerById(feat.layer.id)?.layerView.id;
            const llConfig = this.llConfigs.filter(lc => lc.lvId === featLvId)[0];

            if (feat.properties?.cluster) {

                const isStandardClusterBreakout = nonClusterCount < LeaderLineLabeler.MAX_LABELS &&
                    feat.properties?.point_count <= LeaderLineLabeler.MAX_CLUSTER_BREAKOUT_SIZE &&
                    expandedClusters.length < LeaderLineLabeler.MAX_CLUSTER_BREAKOUT_ATTEMPTS;

                // an exception to the rule: if we have clusters but not much else on screen then we'll label a few big clusters
                const isBigClusterBreakoutEligible = nonClusterCount < LeaderLineLabeler.MAX_LABELS / 2 && clusterCount > 0;

                if (
                    (isStandardClusterBreakout) ||
                    (isBigClusterBreakoutEligible && bigClusterBreakoutCount < LeaderLineLabeler.MAX_CLUSTER_BREAKOUT_SIZE)
                ) {

                    if(nonClusterCount < LeaderLineLabeler.MAX_LABELS / 2 && clusterCount > 0){
                        bigClusterBreakoutCount++;
                    }

                    expandedClusters.push(feat.properties?.point_count);
                    const pointLayer = llConfig.mapLayer as PointLayer;
                    const promise = pointLayer.getClusterLeavesFeats(feat.properties?.cluster_id).then(feats => {
                        // only take the first x of the cluster
                        feats = feats.slice(0, LeaderLineLabeler.MAX_CLUSTER_BREAKOUT_SIZE);
                        // need to copy:
                        //     1. source prop from clustered feat to each leaf feat for processing later
                        //     2. cluster_id for breaking managing choosing better directions when labelling a cluster
                        //     3. point_count to determine how much we need to shorten the ll for number-cluster labels
                        // todo: put these props in properties rather than root
                        const featsWithSource = feats.map(childFeat => ({
                            ...childFeat,
                            layer: feat.layer,
                            geometry: feat.geometry,
                            cluster_id: feat.properties?.cluster_id,
                            point_count: feat.properties?.point_count,
                        }));
                        breakoutClusterFeats.push(...featsWithSource);
                    });
                    clusterBreakoutPromises.push(promise);
                } else {
                    skippedClusters.push(feat.properties?.point_count);
                }
            }

            const point = NavUtils.geoToPixel(this.baseMap, GeomUtils.getFeatCentrePoint(feat));

            let featRadius = Math.floor(llConfig.featSize / 2);
            if(llConfig.mapLayer.layerView.styleSettings?.sizeThematicSettings?.enabled){
                featRadius = 25;
            }

            const pointBounds = [
                point.x - featRadius,
                point.y - featRadius,
                point.x + featRadius,
                point.y + featRadius
            ];
            const featKey = feat?.layer?.source + "_" + feat.properties?.ES_ID;
            this.addBounds(pointBounds, {properties: {point, featKey}});
        }

        // wait for all cluster breakouts. this might take a while, especially with pbf/100k where clusterLeaves need to be fetched from th server
        await Promise.all(clusterBreakoutPromises);

        features.push(...breakoutClusterFeats);

        // filter positionsToTry by reducing attempt
        const maxAttempts = this.rbush.all().features.length < LeaderLineLabeler.MAX_LABELS ?
            LeaderLineLabeler.MAX_ATTEMPTS : LeaderLineLabeler.MIN_ATTEMPTS;

        // track current cluster being processed. we will only need one of these mappings at a time as the feats in a
        // cluster are all pushed to the features array at the same time and it is not sorted after that.
        let currentClusterTracker: ClusterPositionTracker | null = null;

        // second, process feats and create labels
        for (let i = 0; i < features.length; i++) {
            const feat = features[i];

            const timeTakenSoFar = (Date.now() - startTime);
            if (timeTakenSoFar > LeaderLineLabeler.MAX_PROCESSING_TIME) {
                // NOTE TO DEVELOPERS: if debugging this code you'll probably want to comment this out or loop exits early
                es.log.error("Time limit for leader line label rendering exceeded: " + es.util.formatMillis(timeTakenSoFar) + ", max: " + LeaderLineLabeler.MAX_PROCESSING_TIME + "ms, after processing " + i + " feats");
                break;
            }

            if(labelFeatures.length > LeaderLineLabeler.MAX_LABELS) {
                es.log.warn("Feature count limit for leader line label rendering exceeded: " + i + "ms, max: " + LeaderLineLabeler.MAX_LABELS);
                break;
            }

            if (feat.properties?.cluster) {
                // skip clustered feats as they're already in breakoutClusteredFeats
                //es.log.log("skipping", feat);
                continue;
            }

            try {
                const featLvId = this.baseMap.getLayerById(feat.layer.id)?.layerView.id;
                const llConfig = this.llConfigs.find(c => c.lvId === featLvId);
                if (!llConfig) {
                    es.log.warn("no llConfig found for feat", "featLvId", featLvId, feat);
                    continue;
                }

                // if feature is from a cluster breakout, get/create the tracker
                if (feat?.cluster_id) {
                    if (!currentClusterTracker || currentClusterTracker.clusterId !== feat.cluster_id) {
                        currentClusterTracker = {
                            clusterId: feat.cluster_id,
                            usedDirections: new Set<string>()
                        };
                    }
                } else {
                    currentClusterTracker = null;
                }

                feat.index = i;
                const proposedPlacement = this.calculateLabelPosition(
                    feat,
                    llConfig,
                    maxAttempts,
                    currentClusterTracker
                );

                if (!proposedPlacement) {
                    //es.log.log("no label pos found for feat", feat);
                    continue;
                }

                const labelGeoCoord = NavUtils.pixelToGeo(this.baseMap, proposedPlacement.labelPosition);

                // set anchor based on offset
                let textAnchor;
                if (proposedPlacement.positionDirection.offset.x > 0) {
                    textAnchor = 'left';
                } else if (proposedPlacement.positionDirection.offset.x < 0) {
                    textAnchor = 'right';
                } else if (proposedPlacement.positionDirection.offset.y > 0) {
                    textAnchor = 'top';
                } else {
                    textAnchor = 'bottom';
                }

                // ES_LABEL is special case for AzureScheduleLayer
                const esLevelKey = Object.keys(feat.properties).find(key => key.startsWith('ES_LEVEL_'));
                let labelText = feat.properties[llConfig.labelAttr] ||
                    feat.properties['label'] ||
                    feat.properties['ES_LABEL'] ||
                    (esLevelKey ? feat.properties[esLevelKey] : null) ||
                    'n/a';

                if (LeaderLineLabeler.DEBUG_SHOW_LABEL_DIRECTION_AND_ATTEMPT) {
                    const debugSuffix = `@${proposedPlacement.positionDirection.direction}-${proposedPlacement.positionDirection.attempt}`;
                    if (labelText.length <= debugSuffix.length) {
                        labelText = labelText + debugSuffix;
                    } else {
                        labelText = labelText.substring(0, labelText.length - debugSuffix.length) + debugSuffix;
                    }

                    feat.properties[llConfig.labelAttr] = labelText;
                    feat.properties['ES_LABEL'] = labelText;

                    //es.log.log("Found pos:", rs.pos.direction, "attempt:", rs.pos.attempt, labelText);
                }

                const labelFeature = {
                    id: feat.id,
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [labelGeoCoord.lng, labelGeoCoord.lat]
                    },
                    properties: {
                        'title': labelText,
                        'text-anchor': textAnchor,
                        'lvId': llConfig.lvId,
                        ...feat.properties
                    }
                };

                // only create leader line if needed and coordinates are valid
                // todo: not sure we need this anymore. was prob only happening for clusters or some other case we're handling now
                if (/*proposedPlacement.needsLeaderLine &&*/
                    feat.geometry.coordinates //&&
                    //feat.geometry.coordinates[0] !== undefined &&
                    //feat.geometry.coordinates[1] !== undefined
                ) {

                    // need to shorten ll for clusters so the lines don't obscure the number
                    const clusterPointCount = feat?.point_count;
                    let shortenLineBy = 1;

                    if (clusterPointCount){
                        if (isPieClusteringOn(llConfig.mapLayer.layerView)) {
                            if (clusterPointCount >= 100) {
                                shortenLineBy = 14;
                            } else if (clusterPointCount >= 10) {
                                shortenLineBy = 12;
                            } else {
                                shortenLineBy = 10;
                            }
                        } else {
                            // todo: need to compensate for dynamic sizing
                            //shortenLineBy = Math.floor(llConfig.featSize / 2);
                            shortenLineBy = 5;
                        }
                    } else if(
                        llConfig.mapLayer instanceof RouteLayer ||
                        llConfig.mapLayer.layerView.styleSettings?.conditionalStyleSettings?.conditionalStyles // bs way to guess we've a daytrip layer
                    ){
                        shortenLineBy = 5;
                    }

                    const pointCp = this.shortenLeaderLine(
                        GeomUtils.getFeatCentrePoint(feat), // [feat.geometry.coordinates[0], feat.geometry.coordinates[1]]
                        [labelGeoCoord.lng, labelGeoCoord.lat],
                        shortenLineBy
                    );

                    let labelCp = [labelGeoCoord.lng, labelGeoCoord.lat];

                    // todo: TOR9-20590 if font outline is off then shorten the labelCp
                    const halo = llConfig.mapLayer.layerView?.styleSettings?.defaultStyle?.text?.fontOutlineColor;
                    if(! isLabelBoxEnabled(llConfig.mapLayer.layerView) && !halo){
                        //es.log.log("shorten LL for no box no halo");
                        let shortenBy = proposedPlacement?.dimensions?.height / 2;
                        if(['E', 'W'].includes(proposedPlacement.positionDirection.direction)){ // could arguably include 'WNW', 'WSW', 'ENE', 'ESE' too
                            // should div by 2 but our label widths can be estimated as far too wide so we don't want to cut the line too short
                            //shortenBy = proposedPlacement.dimensions.width / 4;
                            shortenBy = proposedPlacement.dimensions.width / 2;
                        }

                        labelCp = this.shortenLeaderLine(
                            [labelGeoCoord.lng, labelGeoCoord.lat],
                            pointCp,
                            shortenBy
                        );
                    }

                    if (LeaderLineLabeler.DEBUG_SHOW_STATISTICS && proposedPlacement.isLeaderLineNeeded) {
                        totalLeaderLinePixelLength += this.getLeaderLineLengthPixels(pointCp, labelCp);
                    }

                    const leaderLineFeat = {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: [pointCp, labelCp]
                        },
                        properties: {
                            'lvId': llConfig.lvId
                        }
                    };

                    let isCrissCross = false;

                    // check for intersection with ALL existing leader lines, regardless of layer
                    if (LeaderLineLabeler.FILTER_CRISSCROSS_LEADER_LINES) {
                        isCrissCross = leaderLineFeats.some(llFeat => {
                            const intersection = lineIntersect(llFeat.geometry, leaderLineFeat.geometry);
                            return intersection?.features?.length > 0;
                        });
                    }

                    if (!isCrissCross) {
                        if(LeaderLineLabeler.ALWAYS_SHOW_LL || proposedPlacement.isLeaderLineNeeded){
                            leaderLineFeats.push(leaderLineFeat);
                        }
                        labelFeatures.push(labelFeature);
                        this.addBounds(this.getBoundsFromPlacement(proposedPlacement.labelPosition, proposedPlacement.dimensions));
                        //es.log.log("Added LLL", proposedPlacement, labelFeature);
                    } else {
                        crissCrossCount++;
                    }
                }
            } catch (error) {
                es.log.warn('Failed to create label for point:', feat, error);
            }
        }

        // Add all features at once
        if (labelFeatures.length > 0) {
            this.labelLayerDs.add(labelFeatures);
            if (leaderLineFeats.length > 0) {
                this.leaderLineLayerDs.add(leaderLineFeats);
            }
        }

        if (LeaderLineLabeler.DEBUG_SHOW_STATISTICS) {
            const statisticsData = {
                queryRenderedFeatCount,
                pxPointsLoaded: this.rbush.all().features.length,
                nonClusterCount,
                clusterCount,
                maxLabels: LeaderLineLabeler.MAX_LABELS,
                expandedClusters: expandedClusters.length ?
                    expandedClusters.length + " clusters, " + expandedClusters.reduce((sum, current) => sum + current, 0) +  " feats (" + expandedClusters.toString() + ")": 0,
                skippedClusters: skippedClusters.length ?
                    skippedClusters.length + " clusters, " + skippedClusters.reduce((sum, current) => sum + current, 0) +  " feats (" + skippedClusters.toString() + ")": 0,
                crissCrossCount,
                totalLeaderLinePixelLength,
                labelCount: labelFeatures.length,
                percentageLabelled: labelFeatures.length && features.length ?
                    ((labelFeatures.length / (features.length - expandedClusters.length)) * 100).toFixed() + '%' : 'n/a',
                queryRenderedFeaturesTime,
                labelTime: es.util.formatMillis(Date.now() - startTime)
            };

            es.log.table(statisticsData);
        }

        return queryRenderedFeatCount;
    }

    // convenience / wrapper methods for working with rbush
    private addBounds(bounds: BBox, options?: {properties?: Properties; id?: Id}) {
        const polygonFeat = bboxPolygon(bounds, options);
        this.rbush.insert(polygonFeat);
        //es.log.log("addBounds", bounds, options);
    }

    private getCollisions(bounds: BBox): Feature[] {
        const collisions = this.rbush.search(bounds);
        //rs = collisions.features.map(f => f.bbox) as BBox[];
        //es.log.log("getCollisions", bounds, collisions.features);
        return collisions.features;
    }

    private hasCollisions(bounds: BBox): boolean {
        const searchPolygon = bboxPolygon(bounds);
        const rs = this.rbush.collides(searchPolygon);
        //es.log.log("hasCollisions", bounds, rs);
        return rs;
    }
}
